import { useHistory } from '@shared/core';
import { Flex } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { useGuestSiteQueryParams } from '@apps/guest/packages/layout-engine/common/utils/useGuestSiteQueryParams';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelRoutePaths, SearchParams } from '../../HotelBooking.routes';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useQueryParamHelper } from '@shared/core/queryString';
import { PartnerHotelSearchRouteQueryParams } from '../HotelSearch/HotelSearch.types';
import { useUtmParameters } from '@shared/utils/utmParameters';

export const BookYourStay: React.FC<{ eventHandle: string }> = wrapWithHotelBookingRedirectProvider(({ eventHandle }) => {
  const history = useHistory();
  const { eventInfo } = useEventInfo();
  const { checkIn: eventStartDate, checkOut: eventEndDate } = calculateStayDates(eventInfo?.finalizedDate?.dateString);
  const { onLoadFailure, onLoadComplete } = useHotelBookingRedirectHealth();
  const queryParams = useGuestSiteQueryParams();
  const telemetry = useGuestSiteTelemetry();
  const hotelRoute = useHotelRoutePaths();

  const queryParamHelper = useQueryParamHelper();
  const utmParamsFromHook = useUtmParameters();

  const getQueryParam = (param: PartnerHotelSearchRouteQueryParams) => queryParamHelper.getValueString(param);

  const utmParams = {
    ...(utmParamsFromHook || {}),
    discountCode: getQueryParam(PartnerHotelSearchRouteQueryParams.discountCode),
  };

  const { hasVisibleAccommodationsEventPage, error, hasVisibleTravelEventPage, hasTravelAccommodationListings, isResolved, matchedCustomPageSlug } = useEventAccommodationsCheck({
    eventHandle,
  });

  useMount(() => {
    telemetry.bookYourStayPageViewed({
      ...queryParams,
    });
  });

  useEffect(() => {
    if (!isResolved) return;

    if (error) {
      onLoadFailure(`BookYourStay: Accommodations visibility check error`, error, {
        url: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { ...(utmParams as SearchParams) }),
      });
      return;
    }

    const params = new URLSearchParams(utmParams as SearchParams);

    if (hasVisibleAccommodationsEventPage) {
      onLoadComplete({ url: `/${eventHandle}/accommodations?hideannouncement=true&` + params.toString() });
    } else if (hasVisibleTravelEventPage && hasTravelAccommodationListings) {
      onLoadComplete({ url: `/${eventHandle}/travel?hideannouncement=true&` + params.toString() });
      // Redirect to custom page which have accommodation related matching keyword in page slug
    } else if (matchedCustomPageSlug) {
      onLoadComplete({ url: `/${eventHandle}/page/${matchedCustomPageSlug}?hideannouncement=true&` + params.toString() });
    } else {
      onLoadComplete({
        url: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { ...(utmParams as SearchParams) }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, eventEndDate, error, eventStartDate, eventHandle, hasVisibleAccommodationsEventPage, hasVisibleTravelEventPage, hasTravelAccommodationListings, isResolved]);

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      height="100vh"
      width="100vw"
    >
      <JoyLogoLoader loaderKey="guest-hotel-booking" />
    </Flex>
  );
});
